import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { FuseConfigService } from '@fuse/services/config';
import {
  AppStatusService,
  AWSS3Service,
  CreateUserRequest,
  KeycloakRealm,
  ManagerService,
  RedisLockKeyType,
  RedisLockService,
  UserService,
} from '@libs/shared/api';
import { convertToFuseScheme } from '@libs/shared/utils/functions/convert-to-fuse-scheme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthService } from '@sprint-backoffice/app/core/auth/auth.service';
import { NavigationService } from '@sprint-backoffice/app/core/navigation/navigation.service';
import {
  CoreApiActions,
  CorePageActions,
  injectCoreFeature,
} from '@sprint-backoffice/app/core/state/core.store';
import { catchError, map, mergeMap, of, tap } from 'rxjs';

@Injectable()
export class CoreEffects {
  actions$ = inject(Actions);
  snackBar = inject(MatSnackBar);
  core = injectCoreFeature();
  router = inject(Router);
  appStatusService = inject(AppStatusService);
  authService = inject(AuthService);
  userService = inject(UserService);
  managerService = inject(ManagerService);
  navigationService = inject(NavigationService);
  s3Service = inject(AWSS3Service);
  fuseConfigService = inject(FuseConfigService);
  http = inject(HttpClient);
  redisLockService = inject(RedisLockService);

  fetchAppStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.fetchAppStatus),
      mergeMap(() => {
        return this.appStatusService.fetchAppStatus().pipe(
          map(appStatus => {
            if (!appStatus.backofficeAppEnabled) {
              this.router.navigateByUrl('/on-boarding').then();
            }
            return CoreApiActions.fetchAppStatusSuccess({ appStatus });
          }),
          catchError(error =>
            of(CoreApiActions.fetchAppStatusFailure({ error: error.message }))
          )
        );
      })
    )
  );

  enableMemberApp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CorePageActions.enableMemberApp),
      mergeMap(() => {
        return this.appStatusService.enableMemberApp().pipe(
          mergeMap(() => this.appStatusService.fetchAppStatus()),
          map(appStatus => {
            this.openSnackBar('Member App 점검을 종료했어요! 🚀');
            return CoreApiActions.enableMemberAppSuccess({ appStatus });
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.enableMemberAppFailure({ error: error.message })
            );
          })
        );
      })
    );
  });

  disableMemberApp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CorePageActions.disableMemberApp),
      mergeMap(() => {
        return this.appStatusService.disableMemberApp().pipe(
          mergeMap(() => this.appStatusService.fetchAppStatus()),
          map(appStatus => {
            this.openSnackBar('Member App 점검을 시작했어요! 🚀');
            return CoreApiActions.disableMemberAppSuccess({ appStatus });
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.disableMemberAppFailure({ error: error.message })
            );
          })
        );
      })
    );
  });

  enableBackofficeApp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CorePageActions.enableBackofficeApp),
      mergeMap(() => {
        return this.appStatusService.enableBackofficeApp().pipe(
          mergeMap(() => this.appStatusService.fetchAppStatus()),
          map(appStatus => {
            this.openSnackBar('백오피스 점검을 종료했어요! 🚀');
            return CoreApiActions.enableBackofficeAppSuccess({ appStatus });
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.enableBackofficeAppFailure({
                error: error.message,
              })
            );
          })
        );
      })
    );
  });

  disableBackofficeApp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CorePageActions.disableBackofficeApp),
      mergeMap(() => {
        return this.appStatusService.disableBackofficeApp().pipe(
          mergeMap(() => this.appStatusService.fetchAppStatus()),
          map(appStatus => {
            this.openSnackBar('백오피스 점검을 시작했어요! 🚀');
            return CoreApiActions.disableBackofficeAppSuccess({ appStatus });
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.disableBackofficeAppFailure({
                error: error.message,
              })
            );
          })
        );
      })
    );
  });

  fetchCurrentUserRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.fetchCurrentUserRole),
      mergeMap(() =>
        of(this.authService.getCurrentUserRole()).pipe(
          map(currentUserRole =>
            CoreApiActions.fetchCurrentUserRoleSuccess({ currentUserRole })
          ),
          catchError(error =>
            of(
              CoreApiActions.fetchCurrentUserRoleFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  fetchAuthUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.fetchAuthUser),
      mergeMap(() => {
        return this.authService.getUserProfile().pipe(
          tap(userProfile => {
            this.core.fetchOrCreateCurrentUser(userProfile);
          }),
          map(userProfile =>
            CoreApiActions.fetchAuthUserSuccess({ userProfile })
          ),
          catchError(error =>
            of(CoreApiActions.fetchAuthUserFailure({ error: error.message }))
          )
        );
      })
    )
  );

  fetchOrCreateCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.fetchOrCreateCurrentUser),
      mergeMap(action =>
        this.userService.fetchUserById(action.userProfile.info.sub).pipe(
          tap(user => this.core.syncCurrentUserRole(user.id)),
          tap(action => this.core.fetchCurrentManager(action.id)),
          map(user => CoreApiActions.fetchOrCreateCurrentUserSuccess({ user })),
          catchError(error => {
            if (error.status === 404) {
              this.core.createCurrentUser(action.userProfile);
              return of(CoreApiActions.fetchCurrentUserNotFound());
            } else {
              return of(
                CoreApiActions.fetchOrCreateCurrentUserFailure({
                  error: error.message,
                })
              );
            }
          })
        )
      )
    )
  );

  fetchCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.fetchCurrentUserById),
      mergeMap(action => {
        return this.userService.fetchUserById(action.userId).pipe(
          tap(user => this.core.syncCurrentUserRole(user.id)),
          map(user => CoreApiActions.fetchCurrentUserByIdSuccess({ user })),
          catchError(error =>
            of(
              CoreApiActions.fetchCurrentUserByIdFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  createCurrentUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CorePageActions.createCurrentUser),
      mergeMap(action => {
        const request = {
          id: action.userProfile.info.sub,
          firstName: action.userProfile.info.given_name,
          lastName: action.userProfile.info.family_name,
          emailAddress: action.userProfile.info.email,
          timezone:
            typeof Intl === 'object' &&
            Intl.DateTimeFormat &&
            Intl.DateTimeFormat().resolvedOptions
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : 'Asia/Seoul',
          languageCode: 'ko',
          googleUser: false,
          appleUser: false,
          realm: KeycloakRealm.SprintBackofficeApp,
        } as CreateUserRequest;
        return this.userService.createUser(request).pipe(
          tap(userId => {
            this.core.fetchCurrentUserById(userId);
            this.core.fetchCurrentManager(userId);
          }),
          map(() => CoreApiActions.createCurrentUserSuccess()),
          catchError(error =>
            of(
              CoreApiActions.createCurrentUserFailure({ error: error.message })
            )
          )
        );
      })
    );
  });

  updateCurrentUserNickname$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CorePageActions.updateCurrentUserNickname),
      mergeMap(action => {
        return this.userService
          .updateUserNickname(action.userId, action.request)
          .pipe(
            tap(userId => this.core.fetchCurrentUserById(userId)),
            map(() => {
              this.openSnackBar('닉네임이 변경되었어요! 🚀');
              return CoreApiActions.updateCurrentUserNicknameSuccess();
            }),
            catchError(error => {
              this.openSnackBar('문제가 발생했어요! 😥');
              return of(
                CoreApiActions.updateCurrentUserNicknameFailure({
                  error: error.message,
                })
              );
            })
          );
      })
    );
  });

  updateCurrentUserProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.updateCurrentUserProfileImage),
      mergeMap(action => {
        return this.s3Service.getPresignedImagePut().pipe(
          mergeMap(presignedPutUrl => {
            return this.http
              .put(presignedPutUrl.url, action.imageFile, {
                headers: { 'Content-Type': action.contentType },
              })
              .pipe(
                mergeMap(() =>
                  this.userService
                    .updateUserProfileImage(action.userId, {
                      profileImageFileKey: presignedPutUrl.fileKey,
                    })
                    .pipe(
                      tap(userId => this.core.fetchCurrentUserById(userId)),
                      map(() => {
                        this.openSnackBar('프로필 이미지가 변경되었어요! 🚀');
                        return CoreApiActions.updateCurrentUserProfileImageSuccess();
                      }),
                      catchError(error => {
                        this.openSnackBar('문제가 발생했어요! 😥');
                        return of(
                          CoreApiActions.updateCurrentUserProfileImageFailure({
                            error: error.message,
                          })
                        );
                      })
                    )
                )
              );
          })
        );
      })
    )
  );

  deleteCurrentUserProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.deleteCurrentUserProfileImage),
      mergeMap(action => {
        return this.userService.deleteUserProfileImage(action.userId).pipe(
          tap(userId => this.core.fetchCurrentUserById(userId)),
          map(() => {
            this.openSnackBar('프로필 이미지가 삭제되었어요! 🚀');
            return CoreApiActions.deleteCurrentUserProfileImageSuccess();
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.deleteCurrentUserProfileImageFailure({
                error: error.message,
              })
            );
          })
        );
      })
    )
  );

  syncCurrentUserRole$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CorePageActions.syncCurrentUserRole),
      mergeMap(action => {
        return this.userService.syncUserRole(action.userId).pipe(
          map(() => CoreApiActions.syncCurrentUserRoleSuccess()),
          catchError(error =>
            of(
              CoreApiActions.syncCurrentUserRoleFailure({
                error: error.message,
              })
            )
          )
        );
      })
    );
  });

  createCurrentManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.createCurrentManager),
      mergeMap(action =>
        this.managerService.createManager(action.request).pipe(
          tap(userId => this.core.fetchCurrentManager(userId)),
          map(() => {
            this.openSnackBar('매니저를 생성했어요! 🚀');
            return CoreApiActions.createCurrentManagerSuccess();
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.createCurrentManagerFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  updateCurrentManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.updateCurrentManager),
      mergeMap(action =>
        this.managerService.updateManager(action.userId, action.request).pipe(
          tap(userId => this.core.fetchCurrentManager(userId)),
          map(() => {
            this.openSnackBar('매니저 정보를 수정했어요! 🚀');
            return CoreApiActions.updateCurrentManagerSuccess();
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.updateCurrentManagerFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  fetchCurrentManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.fetchCurrentManager),
      mergeMap(() =>
        this.managerService.fetchManagerById(this.core.currentUserId$$()).pipe(
          tap(manager => {
            this.fuseConfigService.config = {
              scheme: convertToFuseScheme(manager.webColorScheme),
            };
            if (manager.contractTerminatedAt) {
              new Date(manager.contractTerminatedAt) <= new Date()
                ? this.router.navigateByUrl('/on-boarding').then()
                : null;
            }
          }),
          map(manager =>
            CoreApiActions.fetchCurrentManagerSuccess({ manager })
          ),
          catchError(error => {
            if (error.status === 404) {
              this.router.navigateByUrl('/on-boarding').then();
              return of(CoreApiActions.fetchCurrentManagerNotFound());
            }
            return of(
              CoreApiActions.fetchCurrentManagerFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  completeManagerEducation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.completeCurrentManagerEducation),
      mergeMap(() =>
        this.managerService.completeManagerEducation().pipe(
          tap(userId => this.core.fetchCurrentManager(userId)),
          map(() => {
            this.openSnackBar('매니저 교육을 완료했어요! 🚀');
            return CoreApiActions.completeCurrentManagerEducationSuccess();
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.completeCurrentManagerEducationFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  completeManagerTest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.completeCurrentManagerTest),
      mergeMap(() =>
        this.managerService.completeManagerTest().pipe(
          tap(userId => this.core.fetchCurrentManager(userId)),
          map(() => {
            this.openSnackBar('매니저 테스트를 완료했어요! 🚀');
            return CoreApiActions.completeCurrentManagerTestSuccess();
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.completeCurrentManagerTestFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  completeManagerOrientation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.completeCurrentManagerOrientation),
      mergeMap(() =>
        this.managerService.completeManagerOrientation().pipe(
          tap(userId => this.core.fetchCurrentManager(userId)),
          map(() => {
            this.openSnackBar('매니저 오리엔테이션을 완료했어요! 🚀');
            return CoreApiActions.completeCurrentManagerOrientationSuccess();
          }),
          catchError(error => {
            this.openSnackBar('문제가 발생했어요! 😥');
            return of(
              CoreApiActions.completeCurrentManagerOrientationFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  fetchNavigationItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.fetchNavigationItems),
      mergeMap(action =>
        this.navigationService.getByUserRole(action.currentUserRole).pipe(
          map(navigationItems =>
            CoreApiActions.fetchNavigationItemsSuccess({ navigationItems })
          ),
          catchError(error =>
            of(
              CoreApiActions.fetchNavigationItemsFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  updateWebColorScheme$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.updateManagerWebColorScheme),
      mergeMap(action =>
        this.managerService
          .updateManagerWebColorScheme(action.webColorScheme)
          .pipe(
            tap(userId => this.core.fetchCurrentManager(userId)),
            map(() => {
              if (action.webColorScheme === 'Auto') {
                this.openSnackBar('자동 모드로 변경했어요! 🚀');
              } else if (action.webColorScheme === 'Light') {
                this.openSnackBar('라이트 모드로 변경했어요! 🚀');
              } else if (action.webColorScheme === 'Dark') {
                this.openSnackBar('다크 모드로 변경했어요! 🚀');
              }
              return CoreApiActions.updateManagerWebColorSchemeSuccess();
            }),
            catchError(error => {
              this.openSnackBar('문제가 발생했어요! 😥');
              return of(
                CoreApiActions.updateManagerWebColorSchemeFailure({
                  error: error.message,
                })
              );
            })
          )
      )
    )
  );

  checkPopulateAllDocumentsLock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CorePageActions.checkPopulateAllDocumentsLock),
      mergeMap(() =>
        this.redisLockService
          .checkLock(RedisLockKeyType.PopulateAllDocuments)
          .pipe(
            map(locked =>
              CoreApiActions.checkPopulateAllDocumentsLockSuccess({ locked })
            ),
            catchError(error =>
              of(
                CoreApiActions.checkPopulateAllDocumentsLockFailure({
                  error: error.message,
                })
              )
            )
          )
      )
    )
  );

  private openSnackBar(message: string) {
    this.snackBar.open(message, '닫기', { duration: 5000 });
  }

  constructor() {}
}
